import { Injectable } from '@angular/core';
import { CalendarEvent } from 'angular-calendar';
import { CalendarUtilsService } from './calendar-utils.service';
import {
  AppointmentCreate,
  AppointmentReadCollectionResource,
  AppointmentUpdate,
} from '../../models/appointment-model';
import { CalendarPatient } from '../../models/patient-model';
import { EventColor } from 'calendar-utils';
import { modifyCalendarEvent } from '../../models/calendar-model';

@Injectable({
  providedIn: 'root',
})
export class CalendarAdapterService {
  colors: Record<string, EventColor> = {
    blue: {
      primary: '#B8D8FF',
      secondary: '#B8D8FF',
    },
    blue_deleted: {
      primary: '#FF7074',
      secondary: '#B8D8FF',
    },
  };
  constructor(private calendarUtils: CalendarUtilsService) {}
  adapterFromGCalEventToAngularCalendarEvent(
    event: any,
    calendarColor: string
  ): CalendarEvent {
    const tempEvent: CalendarEvent = {
      id: event.id,
      start: new Date(
        event.start.dateTime ? event.start.dateTime : event.start.date
      ),
      end: new Date(event.end.dateTime ? event.end.dateTime : event.end.date),
      title: event.summary,
      color: {
        primary: calendarColor,
        secondary: calendarColor,
      },
      draggable: true,
      resizable: {
        beforeStart: true,
        afterEnd: true,
      },
      allDay: event.start.dateTime ? false : true,
    };

    return tempEvent;
  }

  adapterFromDbAppointmentToAngularCalendarEvent(
    appointment: AppointmentReadCollectionResource,
    patient: CalendarPatient
  ): CalendarEvent {
    const combinedDate = this.calendarUtils.combineDateTime(
      appointment.date,
      appointment.start_time
    );
    var combinedEndDate: Date;
    if (appointment.end_time && appointment.end_time !== '') {
      combinedEndDate = this.calendarUtils.combineDateTime(
        appointment.date,
        appointment.end_time
      );
    } else {
      // FIXME: we should never go inside this else. Make sure the backend returns the end_time for every appointment
      combinedEndDate = this.calendarUtils.addOneHour(combinedDate);
    }

    const appointmentTitle =
      patient &&
      appointment.title &&
      !appointment.title.toLowerCase().includes(patient.last_name.toLowerCase())
        ? `${patient.first_name} ${patient.last_name}\: ${appointment.title}`
        : appointment.title;

    const tempEvent: CalendarEvent = {
      id: appointment.id as string,
      start: new Date(combinedDate),
      end: new Date(combinedEndDate),
      title: appointmentTitle,
      draggable: true,
      resizable: {
        beforeStart: true,
        afterEnd: true,
      },
      color: appointment.google_calendar_event_id
        ? { ...this.colors['blue'] }
        : { ...this.colors['blue_deleted'] },
    };

    return tempEvent;
  }

  adapterFromAngularCalendarEventToDbAppointmentForUpdate(
    event: CalendarEvent
  ): AppointmentUpdate {
    const date = `${event.start.getDate().toString().padStart(2, '0')}/${(
      event.start.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}/${event.start.getFullYear()}`;
    let start_time: string;
    let end_time: string;
    if (event.allDay) {
      start_time = '00:00';
      end_time = '23:59';
    } else {
      start_time = `${event.start
        .getHours()
        .toString()
        .padStart(2, '0')}:${event.start
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
      end_time = `${event.end
        ?.getHours()
        .toString()
        .padStart(2, '0')}:${event.end
        ?.getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }

    var objForPutAppointment = {
      date: date,
      start_time: start_time,
      end_time: end_time,
      title: event.title,
    };

    return objForPutAppointment;
  }

  adapterFromAngularCalendarEventToDbAppointmentForCreate(
    event: CalendarEvent,
    patientId: string
  ): AppointmentCreate {
    const date = `${event.start.getDate().toString().padStart(2, '0')}/${(
      event.start.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}/${event.start.getFullYear()}`;

    let start_time: string;
    let end_time: string;
    if (event.allDay) {
      start_time = '00:00';
      end_time = '23:59';
    } else {
      start_time = `${event.start
        .getHours()
        .toString()
        .padStart(2, '0')}:${event.start
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

      end_time = `${event.end
        ?.getHours()
        .toString()
        .padStart(2, '0')}:${event.end
        ?.getMinutes()
        .toString()
        .padStart(2, '0')}`;
    }

    let newAppointmentObj = {
      patient_id: patientId,
      date: date,
      start_time: start_time,
      end_time: end_time,
      title: event.title,
      google_calendar_event_id: event.id,
      is_remote: true,
    };

    return newAppointmentObj;
  }

  adapterFromAngularCalendarEventToGoogleCalendarModify(
    event: CalendarEvent,
    newTitle?: string
  ): modifyCalendarEvent {
    return {
      id: event.id as string,
      title: newTitle || event.title,
      date: this.calendarUtils.formatDateFromDateObject(event.start),
      start_time: this.calendarUtils.formatTimeFromDateObject(event.start),
      end_time: event.end
        ? this.calendarUtils.formatTimeFromDateObject(event.end)
        : '',
    };
  }
}
